import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStoreActions } from '~/contexts';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { isObject } from '~/infra/utils';
import { useAgreementsAiSearchStore } from './useAgreementsAiSearchStore';
const ERROR_CODES = new Set([
    'llm_timeout',
    'llm_error',
    'llm_invalid_response',
    'llm_schema_validation_failed'
]);
const isErrorCode = (value)=>typeof value === 'string' && ERROR_CODES.has(value);
const errorCodeFromResponseBody = (body)=>isObject(body) && isErrorCode(body.error) ? body.error : 'unknown';
const AGREEMENTS_AI_SEARCH_TIMEOUT_MS = 10000;
const ABORT_ERROR_NAMES = new Set([
    'CanceledError',
    'AbortError'
]);
const ABORT_ERROR_CODES = new Set([
    'ERR_CANCELED'
]);
const TIMEOUT_ERROR_CODES = new Set([
    'ECONNABORTED',
    'ETIMEDOUT'
]);
const isAbortError = (err)=>isObject(err) && (typeof err.name === 'string' && ABORT_ERROR_NAMES.has(err.name) || typeof err.code === 'string' && ABORT_ERROR_CODES.has(err.code));
const isTimeoutError = (err)=>isObject(err) && typeof err.code === 'string' && TIMEOUT_ERROR_CODES.has(err.code);
export const useAgreementsAiSearchSubmit = (onExtracted)=>{
    const { api } = useStoreActions();
    // WHY: render-stable box for the in-flight AbortController — useState not useRef (useRef-mirror is banned).
    const [inFlight] = useState(()=>({
            controller: null
        }));
    const { setSubmitting, setFilter, setError, setAbortInFlight } = useAgreementsAiSearchStore(useShallow((s)=>({
            setSubmitting: s.setSubmitting,
            setFilter: s.setFilter,
            setError: s.setError,
            setAbortInFlight: s.setAbortInFlight
        })));
    useEffect(()=>()=>{
            var _inFlight_controller;
            return (_inFlight_controller = inFlight.controller) === null || _inFlight_controller === void 0 ? void 0 : _inFlight_controller.abort();
        }, [
        inFlight
    ]);
    return useCallback(async (rawQuery)=>{
        var _inFlight_controller;
        const query = rawQuery.trim();
        if (query.length === 0) return;
        (_inFlight_controller = inFlight.controller) === null || _inFlight_controller === void 0 ? void 0 : _inFlight_controller.abort();
        const controller = new AbortController();
        inFlight.controller = controller;
        setAbortInFlight(()=>controller.abort());
        setSubmitting(true);
        try {
            const response = await api.agreementsAiSearch.invoke({
                query,
                signal: controller.signal,
                timeoutMs: AGREEMENTS_AI_SEARCH_TIMEOUT_MS
            });
            if (controller.signal.aborted) return;
            if (response.tag === 'cannotExtract') {
                setError('cannot_extract');
                return;
            }
            setFilter(response.filter, query);
            onExtracted === null || onExtracted === void 0 ? void 0 : onExtracted(response.filter, query);
        } catch (err) {
            if (isAbortError(err) || controller.signal.aborted) return;
            if (isTimeoutError(err)) {
                setError('llm_timeout');
                return;
            }
            setError(errorCodeFromResponseBody(new ErrorResponse(err).body));
        } finally{
            if (inFlight.controller === controller) {
                setSubmitting(false);
                setAbortInFlight(null);
                inFlight.controller = null;
            }
        }
    }, [
        inFlight,
        setAbortInFlight,
        setSubmitting,
        setError,
        setFilter,
        onExtracted
    ]);
};
